import Head from 'next/head';
import Router from 'next/router';
import type { NextPageContext } from 'next';
import smoothscroll from 'smoothscroll-polyfill';
import { InView } from 'react-intersection-observer';
import { createRef, PropsWithChildren, RefObject, useEffect, useState } from 'react';

import type {
  Bundle,
  BundleMedia,
  Item,
  NormalizedProductReview,
  ProductMedia,
  SeoData,
  YotpoAlbumWithImages,
} from 'types';
import tw from '@root/tailwind.config';
import { ProductAssociation, ProductAssociationWithImage } from '@root/server/services/cms/Cms';
import {
  findSanityBundleById,
  findSanityProductByCatalogNumber,
  getProductPageSettings,
} from '@root/server/services/Sanity';
import CustomizeBox from 'components/CustomizeBox';
import IconCalloutGrid from 'components/IconCalloutGrid';
import IconCheck from 'components/IconCheck';
import IconX from 'components/IconX';
import Layout1x1 from 'components/Layout1x1';
import Loading from 'components/Loading';
import Modal from 'components/Modal';
import { PortableText } from 'components/sanity/serializers';
import productCategories, { CategoryDto } from 'data/Products';
import { getItemPageSchema } from 'data/PageSchema';
import TitleGroup from 'partials/products/TitleGroup';
import ProductReviews from 'partials/products/ProductReviews';
import { ProductAssociations } from 'partials/products/ProductAssociations';
import { ProductNavigation } from 'partials/products/ProductNavigation';
import VideoThumbnail from 'partials/products/VideoThumbnail';
import productsApi, {
  getBundles,
  getBundleBySlug,
  getProducts,
  getProductAssociations,
  getProductBySlug,
  getSurroundingProductsInCategory,
  SurroundingProducts,
} from 'services/productsApi';
import { withPageStaticProps } from 'utils/header';
import { productsViewed } from 'utils/metrics';
import {
  hasCurrentBlockOutDates,
  getJacketSkuFromSkuList,
  getSEO,
  getItemUrl,
  hasCurrentBlockOutDatesByType,
  getVisualizerQueryString,
} from 'utils/utils';
import { getAlbumById } from '@root/server/services/Yotpo';
import { useCustomerEventList } from 'components/CustomerEventList';
import YotpoImageGallery from 'components/yotpo/ImageGallery';
import type { YotpoUgcGallery as YotpoUgcGallerySchema } from '@root/sanity/sanity.types';
import BundleRadioPrices from 'components/BundleRadioPrices';

const ContainerCustom = ({ className, ...props }: PropsWithChildren<ClassNameProp>) => (
  <div {...props} className={`mx-auto w-full max-w-screen-lg  px-16 xs:px-32 ${className ? className : ''}`}>
    {props.children}
  </div>
);

const createColumnSpan = (index: number, total: number) => {
  const checkIndex = (index: number) => {
    return ((index + 1) % 3) - 1;
  };
  const isFullWidth = checkIndex(index) === 0;
  const isLastItem = index === total - 1;
  const followsFullWidthItem = checkIndex(index - 1) === 0;

  if (isLastItem && followsFullWidthItem) {
    return 'col-span-2';
  }

  if (isFullWidth) {
    return 'col-span-2';
  } else {
    return 'col-span-1';
  }
};

const scrollX = (elmId: string, container: RefObject<HTMLElement> | null, offset = 0) => {
  const elmOffset = document.querySelector(`#${elmId}`);

  if (container) {
    container.current!.scrollTo({
      left: (elmOffset as HTMLElement)!.offsetLeft + offset,
      behavior: 'smooth',
    });
  }
};

const scrollWindow = (elmId: string, offset = 0) => {
  const elmOffset = document.querySelector(`#${elmId}`);
  window.scrollTo({
    top: (elmOffset as HTMLElement).offsetTop + offset,
    behavior: 'smooth',
  });
};

const getProductReviews = async (id: number, page = 1): Promise<NormalizedProductReview> => {
  const response = await productsApi.getProductReviews(page, id);

  const { reviews } = response.productReviews;
  const { averageScore, totalReview } = response.productReviews.bottomLine;

  return {
    data: reviews,
    totalReviewCount: totalReview,
    averageStarRating: averageScore,
    page: page,
  };
};

const formatProductAssociations = (
  bundles: Bundle[],
  productAssociations: ProductAssociation[]
): ProductAssociationWithImage[] | null => {
  const IMAGE_LABEL = 'PDP_Hero';
  const rtnAssociations = [] as ProductAssociationWithImage[];

  for (const p of productAssociations) {
    const matchedBundle = bundles.find((b) => b.id === p.bundleId);
    const image = matchedBundle?.media?.find((m) => m.label === IMAGE_LABEL)?.url;

    if (image) {
      rtnAssociations.push({ imageUrl: encodeURI(image), ...p });
    }
  }

  return rtnAssociations.length ? rtnAssociations : null;
};

const highlightThumbnail = (
  inView: boolean,
  thumbId: string,
  thumbRowRef: RefObject<HTMLDivElement> | null = null,
  axis = 'y'
) => {
  const selector = axis === 'x' ? `#${thumbId}` : `[data-value='${thumbId}']`;

  // toggle class of thumbnails to match big items
  const thumbnailElm = document.querySelector(selector);
  if (inView) {
    thumbnailElm!.classList.add('border-b-gold');
    thumbnailElm!.querySelector('img')?.classList.add('opacity-30');
  } else {
    thumbnailElm!.classList.remove('border-b-gold');
    thumbnailElm!.querySelector('img')?.classList.remove('opacity-30');
  }

  // scroll thumbnail row to match big row
  axis === 'x' && scrollX(thumbId, thumbRowRef, -80);
};

const urlToId = (url: string) => url.replace(/[:\/\.]/g, '_');

const isHTOAvailableForBundle = (bundle: Bundle) => !bundle.is_retail || bundle.rental_bundle;

const hasAvailableSwatches = (item: Item) => {
  if (!item.swatch || !item.swatch.displayable) {
    return false;
  }

  const blockoutDates = item.swatch?.blockoutDates ?? item.swatch?.blockout_dates ?? [];

  return hasCurrentBlockOutDates(blockoutDates) === false;
};

const getItemNameForTrackerClass = (item: Item) => {
  const name = item.display_name ?? item.displayName ?? '';

  return name.replace(/ /g, '_').toLowerCase();
};

type YotpoUgcGallerySection = Pick<YotpoUgcGallerySchema, 'header'> & Pick<YotpoAlbumWithImages, 'images'>;

type Props = {
  isBundle: boolean;
  images: (ProductMedia | BundleMedia)[];
  marketingCopy?: [object] | null;
  product: Item;
  productAssociationsWithImages: ProductAssociationWithImage[];
  reviews: NormalizedProductReview;
  seo: SeoData;
  sku: string;
  surroundingProducts: SurroundingProducts;
  visualizerQueryString: string;
  yotpoUgcGallery: YotpoUgcGallerySection | null;
};

type MediaQueryHandler = {
  (self: MediaQueryList, event?: MediaQueryListEvent): void;
};

const Product = (props: Props): JSX.Element => {
  const [isZoomOpen, setIsZoomOpen] = useState<boolean>(false);
  const [zoomedImg, setZoomedImg] = useState<string>('');
  const [minBreakpointSm, setMinBreakpointSm] = useState<boolean>(false);
  const [isModalImageLoaded, setIsModalImageLoaded] = useState<boolean>(false);
  const [scrollPosition, setScrollPosition] = useState<number>(0);
  const [reviews, setReviews] = useState<NormalizedProductReview>(props.reviews);

  const { primary: primaryEvent } = useCustomerEventList();

  const toggleZoom = (imgId?: string, scrollPos?: number) => {
    setScrollPosition(scrollPos || scrollPos === 0 ? scrollPos : scrollPosition);
    setIsZoomOpen(!isZoomOpen);
    setZoomedImg(imgId ? imgId : '');
    setIsModalImageLoaded(false);
  };

  useEffect(() => {
    setReviews(props.reviews);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.product.id]);

  useEffect(() => {
    Router.beforePopState(() => {
      if (isZoomOpen) {
        toggleZoom();
      }
      return true;
    });

    let sku = '';
    if (props.product.type === 'tuxedo' || props.product.type === 'suit') {
      sku = getJacketSkuFromSkuList(props.sku);
    } else {
      sku = props.product.sku;
    }

    productsViewed([sku], {
      sku: sku ? sku : props.sku,
      category: props.product.category,
      name: props.product.display_name,
      variant: props.product.color,
      price: Number(props.product.cost),
      position: props.product.display_index,
      url: window.location.href,
      value: Number(props.product.cost),
      currency: 'usd',
    });

    // allows smooth scrolling in all browsers
    smoothscroll.polyfill();

    // js media queries
    const mediaQuery: MediaQueryList = window.matchMedia(`(min-width: ${tw.theme.screens.sm})`);

    const handleMediaQuery: MediaQueryHandler = (e: MediaQueryList) => {
      setMinBreakpointSm(e.matches);
    };

    // need to set initial state once because eventListener doesn't fire on load
    handleMediaQuery(mediaQuery);
    // @ts-ignore - do not update this to addEventListener. breaks on iOS 12
    mediaQuery.addListener(handleMediaQuery);

    return function cleanup() {
      // @ts-ignore
      mediaQuery.removeListener(handleMediaQuery);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadMoreReviews = async () => {
    const moreReviews = await getProductReviews(props.product.id, reviews.page + 1);
    setReviews({
      ...moreReviews,
      data: [...reviews.data, ...moreReviews.data],
      page: reviews.page + 1,
    });
  };

  const isBlockedOut = hasCurrentBlockOutDatesByType(props.product, 'general', primaryEvent?.startDate ?? new Date());
  const isHTOBlockedOut = hasCurrentBlockOutDatesByType(props.product, 'hto', primaryEvent?.startDate ?? new Date());
  const isComingSoonBlockedOut = hasCurrentBlockOutDatesByType(
    props.product,
    'coming-soon',
    primaryEvent?.startDate ?? new Date()
  );

  // visibility of callouts, callout for free rental viewable and rent/buy prices
  const isActiveDisplayable = props.product.displayable && (props.product.is_active || props.product.isActive);

  const calloutShippingViewable =
    isActiveDisplayable && (props.product.type === 'tuxedo' || props.product.type === 'suit');

  const calloutSwatchViewable = isActiveDisplayable && hasAvailableSwatches(props.product);

  const isProductHTOCandidate = props.isBundle ? isHTOAvailableForBundle(props.product) : true;

  const calloutHTOViewable = isActiveDisplayable && !isHTOBlockedOut && isProductHTOCandidate;

  // refs for scrolling features
  const bigRowRef = createRef<HTMLDivElement>();
  const thumbRowRef = createRef<HTMLDivElement>();

  const isSuitOrTux = props.product.type === 'tuxedo' || props.product.type === 'suit';

  return (
    <>
      <Head>
        <meta name="description" key="description" content={props.seo.metaDescription} />
        <meta property="og:type" key="og:type" content="product" />
        <title>{props.seo.title}</title>
      </Head>

      <Modal isOpen={isZoomOpen} scrollPosition={scrollPosition} onClose={() => toggleZoom()}>
        {props.images
          .filter((m) => urlToId(m.url!) === zoomedImg)
          .map((m, key) => (
            <div key={key} className="modal-grid flex cursor-zoom-out justify-center" onClick={() => toggleZoom()}>
              <picture>
                <img
                  alt={props.product.display_name}
                  src={encodeURI(`${m.url}?fm=jpg&fit=crop&w=1200&ar=5:7&auto=format`)}
                  className={`transition duration-300 ease-out ${
                    isModalImageLoaded ? 'scale-100 opacity-100' : 'scale-75 opacity-0'
                  }`}
                  onLoad={() => {
                    setIsModalImageLoaded(true);
                  }}
                />
              </picture>
              {!isModalImageLoaded && <Loading className="grid h-screen place-items-center" />}
            </div>
          ))}
      </Modal>
      <ContainerCustom className="product-detail-page mt-32 mb-64 sm:mt-64 sm:mb-128">
        <div className="row">
          <div className="col-span-12 sm:col-span-8">
            {props.images.length > 1 ? (
              <div className="row items-start">
                <div className="sticky top-32 hidden sm:col-span-3 sm:flex sm:flex-col md:col-span-2">
                  <div className="grid grid-cols-2 gap-8">
                    {props.images.map((m) => (
                      <div
                        className="cursor-pointer border-b-2 border-transparent"
                        key={m.url}
                        id={`scroll-thumb-y-${urlToId(m.url!)}`}
                        data-value={`scroll-${urlToId(m.url!)}`}
                        onClick={() => scrollWindow(`scroll-${urlToId(m.url!)}`)}
                      >
                        {m.url?.includes('.mp4') ? (
                          <VideoThumbnail src={m.url} />
                        ) : (
                          <picture>
                            <source
                              media={`(min-width: ${tw.theme.screens.sm})`}
                              srcSet={`${m.url}?fm=jpg&fit=crop&w=80&h=80&auto=format`}
                            />
                            <img
                              className="block transition-opacity duration-200 ease-in-out"
                              src={`${m.url}?fm=jpg&fit=crop&w=80&h=80&auto=format`}
                              alt={props.product.display_name}
                              loading="lazy"
                            />
                          </picture>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="col-span-12 overflow-x-hidden sm:col-span-9 md:col-span-10">
                  <div className="mb-16 block sm:hidden">
                    <TitleGroup
                      isBlockedOut={isBlockedOut}
                      product={props.product}
                      reviews={reviews}
                      displayTitleAsH2
                    />
                  </div>

                  <div
                    className={`scrolling-touch scrollbar-none flex overflow-x-auto sm:grid sm:grid-cols-2 sm:gap-16`}
                    ref={bigRowRef}
                  >
                    {props.images.map((m, index) => (
                      <InView
                        key={m.url}
                        className={`mr-8 sm:mr-0 image-num-${index + 1} ${createColumnSpan(
                          index,
                          props.images.filter((m) => m.label !== 'JACKET_FRONT_LOOK').length
                        )}`}
                        as="div"
                        threshold={0}
                        rootMargin={minBreakpointSm ? `0% 100% -98% 0%` : '0% -50% 0% 0%'}
                        onChange={(inView, _, id = urlToId(m.url!)) => {
                          highlightThumbnail(inView, `scroll-thumb-x-${id}`, thumbRowRef, 'x');
                          highlightThumbnail(inView, `scroll-${id}`);
                        }}
                        role="button"
                      >
                        <div
                          id={'scroll-' + urlToId(m.url!)}
                          onClick={() => {
                            !m.url?.includes('.mp4') && toggleZoom(`${urlToId(m.url!)}`, window.scrollY);
                          }}
                          className={`w-screen-75 sm:w-auto ${m.url?.includes('.mp4') ? '' : 'cursor-zoom-in'}`}
                        >
                          {m.url?.includes('.mp4') ? (
                            <video
                              src={m.url}
                              autoPlay
                              muted
                              playsInline
                              loop
                              preload="none"
                              className="h-auto w-full"
                            />
                          ) : (
                            <picture>
                              <source
                                media={`(min-width: ${tw.theme.screens.md})`}
                                srcSet={`${m.url}?fm=jpg&fit=crop&w=775&ar=5:7&auto=format`}
                              />
                              <source
                                media={`(min-width: ${tw.theme.screens.sm})`}
                                srcSet={`${m.url}?fm=jpg&fit=crop&w=447&ar=5:7&auto=format`}
                              />
                              <img
                                className="w-full"
                                alt={props.product.display_name}
                                src={encodeURI(`${m.url}?fm=jpg&fit=crop&w=400&ar=5:7&auto=format`)}
                                loading={index > 3 ? 'lazy' : undefined}
                              />
                            </picture>
                          )}
                        </div>
                      </InView>
                    ))}
                  </div>

                  <div className="-mr-16 flex w-full overflow-x-auto sm:hidden" ref={thumbRowRef}>
                    {props.images.map((m) =>
                      m.url?.includes('.mp4') ? (
                        <VideoThumbnail
                          key={m.url}
                          id={`scroll-thumb-x-${urlToId(m.url!)}`}
                          data-value={`scroll-${urlToId(m.url!)}`}
                          onClick={() => scrollX(`scroll-${urlToId(m.url!)}`, bigRowRef)}
                          src={m.url}
                          className="relative mt-8 mr-8 cursor-pointer border-b-2 border-transparent"
                        />
                      ) : (
                        <div
                          className="relative mt-8 mr-8 cursor-pointer border-b-2 border-transparent"
                          key={m.url}
                          id={`scroll-thumb-x-${urlToId(m.url!)}`}
                          data-value={`scroll-${urlToId(m.url!)}`}
                          onClick={() => scrollX(`scroll-${urlToId(m.url!)}`, bigRowRef)}
                        >
                          <img
                            className="block h-40 w-40 max-w-none transition-opacity duration-200 ease-in-out"
                            key={m.url}
                            alt={props.product.display_name}
                            loading="lazy"
                            src={encodeURI(`${m.url}?fm=jpg&fit=crop&w=80&h=80&auto=format`)}
                          />
                        </div>
                      )
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className="mb-16 block sm:hidden">
                  <TitleGroup isBlockedOut={isBlockedOut} product={props.product} reviews={reviews} displayTitleAsH2 />
                </div>
                {props.images.map((m, i) => (
                  <div key={i}>
                    <picture key={urlToId(m.url!)}>
                      <source
                        media={`(min-width: ${tw.theme.screens.lg})`}
                        srcSet={`${m.url}?fit=max&w=1000&auto=format`}
                      />
                      <img
                        src={encodeURI(`${m.url}?fit=max&w=600&auto=format`)}
                        alt={props.product.display_name}
                        loading="lazy"
                      />
                    </picture>
                  </div>
                ))}
              </>
            )}
          </div>

          <div className="col-span-12 sm:col-span-4 sm:-mt-16 md:-mt-32">
            <div className="hidden">Style&nbsp;No. {props.sku.replace(',', '-')}</div>

            <div className="z-10 space-y-16 bg-white py-16 sm:sticky sm:top-0 sm:pb-32 md:pt-32">
              <div className="hidden sm:block">
                <TitleGroup isBlockedOut={isBlockedOut} product={props.product} reviews={reviews} />
              </div>
              <div
                className={`${
                  !isActiveDisplayable && props.isBundle ? 'hidden sm:block' : ''
                } fixed bottom-32 left-16 right-16 z-50 bg-white ${
                  props.isBundle ? `px-16 pb-16` : `p-16`
                } shadow-4 sm:static sm:left-auto sm:right-auto sm:p-0 sm:shadow-none`}
              >
                <CustomizeBox
                  item={props.product}
                  isBlockedOut={!!isBlockedOut}
                  isComingSoonBlockedOut={!!isComingSoonBlockedOut}
                  builderQueryString={props.visualizerQueryString}
                  showHtoBtn={(!!calloutHTOViewable && !isComingSoonBlockedOut) ?? false}
                  htoUrl={`${process.env.NEXT_PUBLIC_APP_URL}/hto/looks/build${props.visualizerQueryString}`}
                  isBundle={props.isBundle}
                />
              </div>
            </div>

            {props.isBundle &&
              (isActiveDisplayable ? (
                <div className="mb-48 sm:hidden">
                  <BundleRadioPrices bundle={props.product} />
                </div>
              ) : (
                <p className="mb-48 sm:hidden">Product No Longer Available</p>
              ))}

            {isActiveDisplayable && (
              <IconCalloutGrid
                showIconShipping={calloutShippingViewable ?? false}
                showIconSwatches={calloutSwatchViewable ?? false}
                swatchesCalloutUrl={`${process.env.NEXT_PUBLIC_APP_URL}/swatch/build?swatches=${props.product.swatch?.sku}`}
                showIconHTO={(calloutHTOViewable && !isComingSoonBlockedOut) ?? false}
                htoCalloutUrl={`${process.env.NEXT_PUBLIC_APP_URL}/hto/looks/build${props.visualizerQueryString}`}
                showIconGroomsFree={isActiveDisplayable ?? false}
                swatch={props.product.swatch!}
              />
            )}

            <div className="space-y-32 xs:px-64 sm:px-0">
              {props.product.available_in_slim !== undefined && props.product.available_in_boys !== undefined && (
                <div className="space-y-8">
                  <h2 className="text-h5 !text-gray-dark">Available In</h2>

                  <ul className="list-none">
                    <li className="mb-0">
                      <span className="absolute -ml-32">
                        <IconCheck className="inline !text-gray-dark" />
                      </span>
                      Modern
                    </li>

                    <li className="mb-0">
                      <span className="absolute -ml-32">
                        {props.product.available_in_slim ? (
                          <IconCheck className="inline !text-gray-dark" />
                        ) : (
                          <IconX className="inline !text-gray" />
                        )}
                      </span>

                      {props.product.available_in_slim ? 'Slim' : <strike>Slim</strike>}
                    </li>

                    <li className="mb-0">
                      <span className="absolute -ml-32">
                        <IconCheck className="inline !text-gray-dark" />
                      </span>
                      Mens
                    </li>

                    <li className="mb-0">
                      <span className="absolute -ml-32">
                        {props.product.available_in_boys ? (
                          <IconCheck className="inline !text-gray-dark" />
                        ) : (
                          <IconX className="inline !text-gray" />
                        )}
                      </span>

                      {props.product.available_in_boys ? 'Boys' : <strike>Boys</strike>}
                    </li>
                  </ul>
                </div>
              )}

              {props.product.details && props.product.details.length > 0 && (
                <div className="space-y-8">
                  <h2 className="text-h5 !text-gray-dark">Details</h2>

                  <ul>
                    {props.product.details.map((d) => (
                      <li key={d.id} className="mb-0">
                        {d.detail}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="space-y-16 whitespace-pre-line">
                <p className="text-sm">{props.product.short_description}</p>

                {props.product.rental_terms && props.product.rental_terms.trim() !== '' && (
                  <p className="text-sm">{props.product.rental_terms}</p>
                )}

                {props.product.type && props.product.type.toLowerCase() === 'tuxedo' && (
                  <p className="text-sm">
                    Our tuxedo pants do not have belt loops and cannot be worn with a belt; however, they do accept
                    clip-in suspenders.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </ContainerCustom>

      {props.yotpoUgcGallery !== null && (
        <YotpoImageGallery
          album={props.yotpoUgcGallery.images}
          heading={props.yotpoUgcGallery.header ?? 'See this look on Generation Tux customers'}
          item={props.product}
          makeTrackerClass={(_) =>
            `tracker-img-product_page-yotpo_lightbox_${getItemNameForTrackerClass(props.product)}-210318-083330`
          }
        />
      )}

      {props.isBundle && props.productAssociationsWithImages ? (
        <div className="p-section bg-gray-lighter">
          <ProductAssociations products={props.productAssociationsWithImages} />
        </div>
      ) : (
        <div className="p-section bg-gray-lighter">
          <ProductNavigation products={props.surroundingProducts} />
        </div>
      )}

      {props.marketingCopy && (
        <Layout1x1 className="m-section">
          <div className="tracker-cta-product_page-seo-20200210-085652 space-y-32">
            <PortableText content={props.marketingCopy} />
          </div>
        </Layout1x1>
      )}

      {isSuitOrTux && (
        <ProductReviews className="p-section bg-gray-lighter" reviews={reviews} onClickLoadMore={loadMoreReviews} />
      )}
    </>
  );
};

type Context = {
  params: {
    product: string;
    category: string | null;
  };
} & NextPageContext;

// This function gets called at build time
export const getStaticPaths = async () => {
  const categories = productCategories.filter((c) => c.slug !== null);
  const paths = await categories.reduce(async (acc: Promise<Context[]>, i: CategoryDto): Promise<Context[]> => {
    const isBundle = i.slug && i.slug.toLowerCase() === 'tuxedos-and-suits';
    const response = isBundle ? await getBundles() : await getProducts(i.name);
    const products = await response.json();

    const params = products.map((b) => ({
      params: {
        product: b.url_slug,
        category: i.slug,
      },
    }));

    // @ts-ignore
    return (await acc).concat(params);
  }, Promise.resolve([] as Context[]));

  return { paths, fallback: false };
};

const getProductImagesSortedByLabel = (product: Item) => {
  return product
    .media!.filter(shouldImageBeIncluded)
    .sort(getSortingPositionByAltLabel)
    .sort(getSortingPositionByHeroLabel);
};

export const getStaticProps = withPageStaticProps(async ({ res, asPath, params }: Context) => {
  const category = params.category ?? '';

  const isBundle = category.toLowerCase() === 'tuxedos-and-suits';

  const getItemBySlug = isBundle ? getBundleBySlug : getProductBySlug;

  const response = await getItemBySlug(params.product);

  if (response.status !== 200 && response.status !== 201) {
    throw new Error(response.statusText);
  }

  const product = await response.json();
  const productAssociations = await (await getProductAssociations((product as Bundle).id)).json();
  const bundles = await (await getBundles()).json();

  const productAssociationsWithImages = formatProductAssociations(bundles, productAssociations);

  if (typeof product === 'string' || (!isBundle && pathHasInvalidCategory(product, category))) {
    throw new Error('404');
  }

  // If no media is attached to bundle, redirect
  // to collection list page
  if (!product.media) {
    const redirectUrl = `/${asPath!.split('/').slice(1, 3).join('/')}`;

    if (res) {
      res.writeHead(302, {
        Location: redirectUrl,
      });

      return res.end();
    } else {
      return Router.push(redirectUrl);
    }
  }

  let marketingCopy = null,
    yotpoUgcGallery: YotpoUgcGallerySection | null = null;

  const productUrl = getItemUrl(product);
  let seoData = await getSEO(productUrl);

  if (!seoData?.metaDescription) {
    seoData.metaDescription = `${product.display_name} | ${product.short_description}`;
  }

  if (isBundle) {
    const sanityBundle = await findSanityBundleById(product.id.toString());

    marketingCopy = sanityBundle?.marketingCopyContentRaw ?? null;

    if (sanityBundle?.pageSeoTitle?.trim()) {
      seoData.title = sanityBundle.pageSeoTitle;
    }

    if (sanityBundle?.pageSeoDescription?.trim()) {
      seoData.metaDescription = sanityBundle.pageSeoDescription;
    }

    if (sanityBundle?.yotpoUgcGallery) {
      const { header, album } = sanityBundle.yotpoUgcGallery;

      const { images } = await getAlbumById(album);

      yotpoUgcGallery = {
        images,
        ...(header ? { header } : {}),
      };
    }
  } else {
    const sanityProduct = await findSanityProductByCatalogNumber(product.sku!);

    marketingCopy = sanityProduct?.marketingCopyContentRaw ?? null;

    if (sanityProduct?.pageSeoTitle) {
      seoData.title = sanityProduct.pageSeoTitle;
    }

    if (sanityProduct?.pageSeoDescription) {
      seoData.metaDescription = sanityProduct.pageSeoDescription;
    }
  }
  return {
    props: {
      isBundle,
      images: getProductImagesSortedByLabel(product),
      marketingCopy,
      product,
      productAssociationsWithImages,
      reviews: getItemReviews(product, isBundle),
      seo: seoData,
      schema: await getItemPageSchema(product, productUrl),
      sku: getItemSku(product, isBundle),
      surroundingProducts: await getSurroundingProductsInCategory(product),
      visualizerQueryString: getVisualizerQueryString(product, isBundle),
      yotpoUgcGallery,
    },
  };
});

const pathHasInvalidCategory = (product: Item, categoryFromPath: string) => {
  const productCategory = (product.category ?? '').toLowerCase();

  const invalidNonAccessoryCategory = !categoryFromPath.includes(productCategory);

  const invalidAccessoryCategory = !(
    productCategory.includes('belt') ||
    productCategory.includes('cufflinks') ||
    productCategory.includes('suspenders') ||
    productCategory.includes('pocket') ||
    productCategory.includes('lapel') ||
    productCategory.includes('bar')
  );

  if (categoryFromPath !== 'accessories') {
    return invalidNonAccessoryCategory;
  }

  return invalidAccessoryCategory;
};

const getItemSku = (product: Item, isBundle: boolean) => {
  if (isBundle) {
    return (product as Bundle).products!.map((p) => p.sku).join(',');
  }

  return product.sku;
};

const getItemReviews = (product: Item, isBundle: boolean) => {
  if (!isBundle) {
    return {
      page: 0,
      data: [],
      totalReviewCount: 0,
      averageStarRating: 0,
    };
  }

  return {
    page: 1,
    data: (product as Bundle).productReviews!.reviews,
    totalReviewCount: (product as Bundle).productReviews!.bottomLine.totalReview,
    averageStarRating: (product as Bundle).productReviews!.bottomLine.averageScore,
  };
};

const shouldImageBeIncluded = (image: ProductMedia) =>
  image.label !== 'JACKET_FRONT_LOOK' &&
  image.label !== 'PGP_QV' &&
  image.label !== 'PDP_Reccos' &&
  image.label!.indexOf('VISUALIZER') === -1;

const getSortingPositionByHeroLabel = (a: ProductMedia, b: ProductMedia) => {
  const aIsHeroLabel = a.label === 'PDP_Hero';
  const bIsHeroLabel = b.label === 'PDP_Hero';

  if (aIsHeroLabel && !bIsHeroLabel) {
    return -1;
  } else if (!aIsHeroLabel && bIsHeroLabel) {
    return 1;
  }

  return 0;
};

const getSortingPositionByAltLabel = (a: ProductMedia, b: ProductMedia) => {
  const pattern = /PDP_Alt([0-9]+)/i;

  const matchesForA = a.label!.match(pattern);
  const matchesForB = b.label!.match(pattern);

  if (matchesForA && matchesForB) {
    // @ts-ignore
    return matchesForA.pop() - matchesForB.pop();
  } else if (matchesForA && !matchesForB) {
    return -1;
  } else if (!matchesForA && matchesForB) {
    return 1;
  }

  return 0;
};

export default Product;
